import {
  ChangeDetectionStrategy,
  Component,
  computed,
  EventEmitter,
  inject,
  input,
  Input,
  InputSignal,
  output,
  Output,
  Signal,
  signal,
  ViewChild,
  WritableSignal,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { FormsModule } from '@angular/forms';
import {
  InvestigationResultDto,
  AssignCase,
  PDropdownOptions,
  User,
} from '../../../interfaces';
import { CaseService } from '../../../services/case.service';
import { API_URL, Roles } from '../../../utils';
import { DialogModule } from 'primeng/dialog';
import { MultiSelectModule } from 'primeng/multiselect';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService, MessageService } from 'primeng/api';
import { NgClass } from '@angular/common';
import { ButtonModule } from 'primeng/button';
import { TextareaModule } from 'primeng/textarea';
import { ToastModule } from 'primeng/toast';
import { SelectModule } from 'primeng/select';
import { ListboxModule } from 'primeng/listbox';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { TranslationService } from '../../../services/translation.service';
import { UserService } from '../../../services';
import { InvestigationResultModalComponent } from '../investigation-result-modal/investigation-result-modal.component';
import { ChangeDetectorRef } from '@angular/core';
import { InvestigatorDto } from '../../../interfaces/case/InvestigatorDto';
import { AssessorsDto } from '../../../interfaces/case/AssessorsDto';
import { PanelModule } from 'primeng/panel';

@Component({
  selector: 'app-actions-card',
  standalone: true,
  imports: [
    FormsModule,
    DialogModule,
    MultiSelectModule,
    ConfirmDialogModule,
    NgClass,
    ButtonModule,
    ListboxModule,
    PanelModule,
    TextareaModule,
    ToastModule,
    SelectModule,
    TranslatePipe,
    InvestigationResultModalComponent
  ],
  templateUrl: './actions-card.component.html',
  styleUrl: './actions-card.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ConfirmationService, MessageService],
})

export class ActionsCardComponent {
  // ============================================================
  // DEPENDENCIES
  // ============================================================

  translateService = inject(TranslationService);
  private readonly translate = inject(TranslateService);
  private readonly userService = inject(UserService);
  private readonly caseService = inject(CaseService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly messageService = inject(MessageService);
  private readonly cdr = inject(ChangeDetectorRef);

  readonly api = inject(API_URL);
  readonly roles = Roles;

  private subscriptions: Subscription[] = [];

  // ============================================================
  // ENVIRONMENT
  // ============================================================

  get isCoassessEnvironment(): boolean {
    return this.api.includes('coassess');
  }

  get isCoreactEnvironment(): boolean {
    return (
      this.api.includes('coreact') ||
      this.api.includes('localhost')
    );
  }

  // ============================================================
  // INPUTS & OUTPUTS
  // ============================================================

  @Input({ required: true }) claimStatus: string = '';
  @Input({ required: true }) currentUser: any;
  @Input({ required: true }) caseData: any;
  @Input({ required: true }) investigationStatuses: any;

  users: InputSignal<User[]> = input.required();
  investigationResults = input.required<InvestigationResultDto[]>();

  @Output() claimStatusChange = new EventEmitter<any>();
  @Output() caseDataChange = new EventEmitter();
  @Output() savedAmountChange = new EventEmitter<number>();
  @Output() investigationCreated = new EventEmitter<void>();

  @Output() externalInvestigationRequested = new EventEmitter<void>();

  openExternalInvestigationModal(): void {
    if (!this.canAssignExternalInvestigator) {
      return;
    }
    this.externalInvestigationRequested.emit();
  }

  investigationResultsChange = output<InvestigationResultDto[]>();

  // ============================================================
  // SHARED STATE
  // ============================================================

  allInvestigationResults = signal<InvestigationResultDto[]>([]);
  showInvestigationResultModal = signal<boolean>(false);

  statusOptions: WritableSignal<PDropdownOptions[]> = signal([
    {
      label: this.translate.instant('CASE_DETAIL.APPROVE'),
      value: 'true',
    },
    {
      label: this.translate.instant('CASE_DETAIL.REJECT'),
      value: 'false',
    },
  ]);

  selectedStatus: string = '';
  selectedComments: any = null;
  temporaryResults: any[] = [];
  currentTemporaryResultId: any;

  selectedInvestigationResult: InvestigationResultDto = {
    comment: '',
    investigationResultId: '',
    investigator: '',
    visibilityStatus: '',
    tags: [],
    files: [],
  };

  selectedTags: any;
  newComment: string | null = null;
  isButtonDisabled: boolean = false;
  toBeUploadedFiles: File[] = [];

  // ============================================================
  // COASSESS STATE & OPTIONS
  // ============================================================

  caseManagerOptions = computed(() =>
    this.userService.caseManagerOptions()
  );

  coassessInvestigatorOptions = computed(() =>
    this.userService.investigatorOptions()
  );

  selectedCaseManagerId: number = 0;

  // ============================================================
  // COREACT / LOCALHOST STATE & OPTIONS
  // ============================================================

  investigators = signal<InvestigatorDto[]>([]);
  assessors = signal<AssessorsDto[]>([]);

  selectedInvestigatorId: number = 0;
  selectedAssessorId: number = 0;

  coreactInvestigatorOptions = computed(() =>
    this.investigators().map(investigator => ({
      label: investigator.name,
      value: investigator.id,
    }))
  );

  assessorOptions = computed(() =>
    this.assessors()
      .filter(assessor => {
        if (!assessor.name) {
          return false;
        }

        const match = assessor.name.match(/^(.*)\((.*)\)$/);

        if (!match) {
          return true;
        }

        const namePart = match[1].trim();
        const codePart = match[2].trim();

        return !(namePart === '' && codePart === '0');
      })
      .map(assessor => ({
        label: assessor.name,
        value: assessor.id,
      }))
  );

  /**
   * Keeps the existing template binding `investigatorOptions()`
   * while making the source of the options environment-specific.
   */
  investigatorOptions = computed(() =>
    this.isCoassessEnvironment
      ? this.coassessInvestigatorOptions()
      : this.coreactInvestigatorOptions()
  );

  openExternalInvestigation = output<void>();

  // ============================================================
  // PERMISSIONS
  // ============================================================

  get isCaseDetailReadOnly(): boolean {
    return (
      this.currentUser?.role === this.roles.Adjuster ||
      this.currentUser?.role === this.roles.Guest ||
      this.currentUser?.role === this.roles.Secretary
    );
  }

  get canAssignInvestigator(): boolean {
    return (
      this.currentUser?.role === this.roles.Administrator ||
      this.currentUser?.role === this.roles.Secretary
    );
  }

  get canAssignExternalInvestigator(): boolean {
    return (
      this.currentUser?.role === this.roles.Administrator ||
      this.currentUser?.role === this.roles.Secretary ||
      `${this.currentUser?.firstName} ${this.currentUser?.lastName}` ===
        this.caseData.investigator
    );
  }

  get isButtonDisabledInvestigation(): boolean {
    return (
      this.investigationResults().length > 0 ||
      !this.caseData.investigator
    );
  }

  // ============================================================
  // LIFECYCLE & INITIALIZATION
  // ============================================================

  ngOnInit(): void {
    this.updateStatusOptions();

    if (this.isCoassessEnvironment) {
      this.initializeCoassess();
    } else if (this.isCoreactEnvironment) {
      this.initializeCoreact();
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  private initializeCoassess(): void {
    this.userService.loadCaseManagerOptions();
  }

  private initializeCoreact(): void {
    this.loadAssessorsByClaimId(this.caseData.claimId);
    this.loadInvestigators();
  }

  // ============================================================
  // COREACT / LOCALHOST — DATA LOADING
  // ============================================================

  loadInvestigators(): void {
    const subscription = this.caseService.getInvestigators().subscribe({
      next: result => this.investigators.set(result),
      error: err => console.error('Failed to load investigators', err),
    });

    this.subscriptions.push(subscription);
  }

  loadAssessorsByClaimId(
    claimId: string = this.caseData.claimId
  ): void {
    const subscription = this.caseService
      .getAssessorsByClaimId(claimId)
      .subscribe({
        next: result => this.assessors.set(result),
        error: err => console.error('Failed to load assessors', err),
      });

    this.subscriptions.push(subscription);
  }

  // ============================================================
  // COASSESS — ASSIGNMENTS
  // ============================================================

  onAssign(userId: string): void {
    if (this.isCaseDetailReadOnly) {
      return;
    }

    const assign: AssignCase = {
      userId,
      caseIds: [this.caseData.caseId],
    };

    const selectedEntity = this.users().find(
      user => user.userId === assign.userId
    );

    const subscription = this.caseService
      .assignToCase(assign)
      .subscribe({
        next: () => {
          const message = this.translate.instant('TOAST.ASSIGN_SUCCESS');
          this.getToastMessage(true, message);
        },
        error: () => {
          const message = this.translate.instant('TOAST.ASSIGN_ERROR');
          this.getToastMessage(false, message);
        },
        complete: () => {
          if (
            selectedEntity &&
            selectedEntity.role === this.roles[this.roles.CaseManager]
          ) {
            this.caseData.caseManager =
              `${selectedEntity.firstName} ${selectedEntity.lastName}`;

            this.caseDataChange.emit(this.caseData);
          } else if (
            selectedEntity &&
            selectedEntity.role === this.roles[this.roles.Investigator]
          ) {
            this.caseData.investigator =
              `${selectedEntity.firstName} ${selectedEntity.lastName}`;

            this.caseData.isPending = false;
            this.caseDataChange.emit(this.caseData);
          } else {
            const investigatorOption = this.investigatorOptions().find(
              option => option.value === assign.userId
            );

            if (investigatorOption) {
              this.caseData.investigator = investigatorOption.label;
              this.caseData.isPending = false;
              this.caseDataChange.emit(this.caseData);
            } else {
              const caseManagerOption = this.caseManagerOptions().find(
                option => option.value === assign.userId
              );

              if (caseManagerOption) {
                this.caseData.caseManager = caseManagerOption.label;
                this.caseDataChange.emit(this.caseData);
              }
            }
          }

          this.cdr.markForCheck();
        },
      });

    this.subscriptions.push(subscription);
  }

  onChangeCaseStatus(caseStatus: string): void {
    const status = caseStatus === 'true';

    const subscription = this.caseService
      .updateApprovalStatus(this.caseData.caseId, status)
      .subscribe({
        next: () => {
          const message = this.translate.instant(
            'TOAST.SUCCESSFULLY_CHANGED'
          );

          this.getToastMessage(true, message);
          this.caseData.isApproved = status;
          this.claimStatus = this.caseData;
          this.caseDataChange.emit(this.claimStatus);
        },
        error: () => {
          const message = this.translate.instant(
            'TOAST.ERROR_CHANGING'
          );

          this.getToastMessage(false, message);
        },
      });

    this.subscriptions.push(subscription);
  }

  // ============================================================
  // COREACT / LOCALHOST — ASSIGNMENTS
  // ============================================================

  onAssignInvestigator(investigatorId: string): void {
    if (!this.canAssignInvestigator) {
      return;
    }

    const assign: AssignCase = {
      userId: investigatorId,
      caseIds: [this.caseData.caseId],
    };

    const subscription = this.caseService
      .assignToCase(assign)
      .subscribe({
        next: () => {
          const message = this.translate.instant('TOAST.ASSIGN_SUCCESS');
          this.getToastMessage(true, message);

          const option = this.investigatorOptions().find(
            item => item.value === investigatorId
          );

          if (option) {
            this.caseData.investigator = option.label;
            this.caseData.isPending = false;
            this.caseDataChange.emit(this.caseData);
          }
        },
        error: () => {
          const message = this.translate.instant('TOAST.ASSIGN_ERROR');
          this.getToastMessage(false, message);
        },
      });

    this.subscriptions.push(subscription);
  }

  onAssignAssessor(assessorId: string): void {
    if (this.isCaseDetailReadOnly) {
      return;
    }

    const assign: AssignCase = {
      userId: assessorId,
      caseIds: [this.caseData.caseId],
    };

    const subscription = this.caseService
      .assignToCase(assign)
      .subscribe({
        next: () => {
          const message = this.translate.instant('TOAST.ASSIGN_SUCCESS');
          this.getToastMessage(true, message);

          const option = this.assessorOptions().find(
            item => item.value === assessorId
          );

          if (option) {
            this.caseData.assessor = option.label;
            this.caseData.isPending = false;
            this.caseDataChange.emit(this.caseData);
          }
        },
        error: () => {
          const message = this.translate.instant('TOAST.ASSIGN_ERROR');
          this.getToastMessage(false, message);
        },
      });

    this.subscriptions.push(subscription);
  }

  clearAssignments(): void {
    if (
      this.isCaseDetailReadOnly &&
      this.currentUser?.role !== this.roles.Secretary
    ) {
      return;
    }

    const EMPTY_GUID = '00000000-0000-0000-0000-000000000000';

    const assign: AssignCase = {
      userId: EMPTY_GUID,
      caseIds: [this.caseData.caseId],
    };

    const subscription = this.caseService
      .assignToCase(assign)
      .subscribe({
        next: () => {
          this.caseData.investigator = null;
          this.caseData.assessor = null;
          this.caseData.isPending = false;

          this.caseDataChange.emit(this.caseData);

          this.selectedInvestigatorId = 0;
          this.selectedAssessorId = 0;
        },
        error: () => {
          const message = this.translate.instant('TOAST.ASSIGN_ERROR');
          this.getToastMessage(false, message);
        },
      });

    this.subscriptions.push(subscription);
  }

  // ============================================================
  // SHARED — INVESTIGATION RESULTS & FILES
  // ============================================================

  createTemporaryInvestigationComment(event: any): void {
    if (this.isCaseDetailReadOnly) {
      return;
    }

    const payload = event?.result ?? event;
    const commentBody = new FormData();
    const tags = payload?.tags ?? [];

    tags.forEach((item: any) => {
      commentBody.append('Tags', item.name ?? item);
    });

    if (payload?.comment) {
      commentBody.append('Comment', payload.comment);
    }

    this.toBeUploadedFiles.forEach((file: File) => {
      commentBody.append('Files', file, file.name);
    });

    this.isButtonDisabled = true;

    const subscription = this.caseService
      .createInvestigatorResult(this.caseData.caseId, commentBody)
      .subscribe({
        next: () => {
        },
        error: error => {
          console.error('Error submitting comment:', error);

          this.newComment = '';
          delete this.selectedTags;

          const message = this.translate.instant(
            'TOAST.SOMETHING_WENT_WRONG'
          );

          this.getToastMessage(false, message);
        },
        complete: () => {
          this.investigationCreated.emit();
          this.cdr.markForCheck();

          this.newComment = '';
          delete this.selectedTags;
        },
      });

    this.subscriptions.push(subscription);
    this.showInvestigationResultModal.set(false);
  }

  handleFilesToUpload(files: File[]): void {
    const file = files[0] || null;
    this.toBeUploadedFiles = file ? [file] : [];

    this.selectedInvestigationResult = {
      ...this.selectedInvestigationResult,
      files: file
        ? [
            {
              fileName: file.name,
              blobUri: '',
              isError: false,
            },
          ]
        : [],
    };
  }

  onRemoveFile(_file: any): void {
    this.toBeUploadedFiles = [];

    this.selectedInvestigationResult = {
      ...this.selectedInvestigationResult,
      files: [],
    };
  }

  handleSavedAmount(amount: number): void {
    this.savedAmountChange.emit(amount);
  }

  // ============================================================
  // SHARED — STATUS OPTIONS
  // ============================================================

  private updateStatusOptions(): void {
    const subscription = this.translateService
      .getTranslations([
        'CASE_DETAIL.APPROVE',
        'CASE_DETAIL.REJECT',
      ])
      .subscribe(translations => {
        this.statusOptions.set([
          {
            label: translations['CASE_DETAIL.APPROVE'],
            value: 'true',
          },
          {
            label: translations['CASE_DETAIL.REJECT'],
            value: 'false',
          },
        ]);
      });

    this.subscriptions.push(subscription);
  }

  // ============================================================
  // SHARED — CONFIRMATION
  // ============================================================

  confirm(): void {
    this.confirmationService.confirm({
      header: 'Confirmation',
      message: 'Please confirm to proceed moving forward.',
      icon: 'pi pi-exclamation-circle',
      rejectButtonProps: {
        label: 'Cancel',
        icon: 'pi pi-times',
        outlined: true,
        size: 'small',
      },
      acceptButtonProps: {
        label: 'Save',
        icon: 'pi pi-check',
        size: 'small',
      },
      accept: () => {
        this.messageService.add({
          severity: 'info',
          summary: 'Confirmed',
          detail: 'You have accepted',
          life: 3000,
        });
      },
      reject: () => {
        this.messageService.add({
          severity: 'error',
          summary: 'Rejected',
          detail: 'You have rejected',
          life: 3000,
        });
      },
    });
  }

  // ============================================================
  // SHARED — TEMPORARY SUBMISSION DIALOGS
  // ============================================================

  async openSubmitModal(): Promise<void> {
    const temporaryResults = this.investigationResults().filter(
      result => result.visibilityStatus === 'Temporary'
    );

    if (temporaryResults.length === 0) {
      alert('Δεν υπάρχουν προσωρινά αποθηκευμένα σχόλια.');
      return;
    }

    this.selectedComments = temporaryResults.flatMap(
      result => result.tags
    );

    this.newComment = temporaryResults
      .map(result => result.comment)
      .join('\n');
  }

  // ============================================================
  // SHARED — TOASTS
  // ============================================================

  getToastMessage(isSuccess: boolean, message?: string): void {
    this.messageService.add({
      severity: isSuccess ? 'success' : 'error',
      summary: isSuccess ? 'Success!' : 'Error!',
      detail: message,
    });
  }
}
